import { useRuntimeConfig } from 'nuxt/app';
import { inject } from 'vue';

import { createNloCommunicationPreferencesApi } from '../api/portal-api/communication-preferences-api';
import { createNloNavigationApi } from '../api/portal-api/navigation-api';
import { createNloPortalApi } from '../api/portal-api/portal-api';
import { createTokenApi } from '../api/portal-api/token-api';
import type { CPCVertical } from '../api/portal-api/vertical-types';

const PORTAL_API_KEY = 'nloPortalApiKey';
const PORTAL_COMMUNICATION_PREF_API_KEY = 'nloPortalCommunicationPreferencesApiKey';
const NAVIGATION_API_KEY = 'nloPortalNavigationApiKey';

/**
 * Provider function that makes sure that NLO portal api's are properly initialized.
 * @param vertical the vertical that is currently rendering the page.
 */
export default function useNloProvideApi(vertical: CPCVertical) {
	const portalBaseUrl = useNloPortalBaseUrl();
	const app = useNuxtApp();
	const fetcherConfigWithoutToken = getNloFetcherConfig({ baseUrl: portalBaseUrl, errorParser: getNloError });
	const tokenApi = createTokenApi(fetcherConfigWithoutToken);
	const fetcherConfig = getNloFetcherConfig({
		baseUrl: portalBaseUrl,
		errorParser: getNloError,
		tokenApiCall: tokenApi.getXSRFToken,
	});
	// Inject values into vueApp, to make sure they are available in pinia stores.
	app.vueApp.provide(PORTAL_API_KEY, createNloPortalApi(fetcherConfig, portalBaseUrl, vertical));
	app.vueApp.provide(PORTAL_COMMUNICATION_PREF_API_KEY, createNloCommunicationPreferencesApi(fetcherConfig));
	app.vueApp.provide(NAVIGATION_API_KEY, createNloNavigationApi(fetcherConfigWithoutToken)); // Without token middleware
}

/**
 * Returns injected Nlo portal api.
 * If there is no api provided, an error will be thrown.
 */
export function useNloPortalApi() {
	const api = inject(PORTAL_API_KEY) as ReturnType<typeof createNloPortalApi>;

	if (!api) {
		throw new Error('Nlo Portal api not provided');
	}
	return api;
}

/**
 * Returns injected Nlo portal communication preferences api.
 * If there is no api provided, an error will be thrown.
 */
export function useNloPortalCommunicationPreferencesApi() {
	const api = inject(PORTAL_COMMUNICATION_PREF_API_KEY) as ReturnType<typeof createNloCommunicationPreferencesApi>;

	if (!api) {
		throw new Error('Nlo Portal communication preferences api not provided');
	}
	return api;
}

/**
 * Returns injected Nlo portal base url.
 * If there is no url provided, an error will be thrown.
 */
export function useNloPortalBaseUrl() {
	const config = useRuntimeConfig();
	const api = config.public.commonPortalBaseUrl;

	if (!api) {
		throw new Error('Nlo Portal base url not provided');
	}
	return api;
}

/**
 * Returns injected Nlo navigation configuration api.
 * If there is no api provided, an error will be thrown.
 */
export function useNloNavigationConfigurationApi() {
	const api = inject(NAVIGATION_API_KEY) as ReturnType<typeof createNloNavigationApi>;

	if (!api) {
		throw new Error('Nlo navigation configuration not provided');
	}
	return api;
}
